import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    PiCheckCircleBold,
    PiXCircleBold,
    PiEnvelopeBold,
    PiPhoneBold,
    PiMapPinBold,
    PiUsersBold,
    PiAnchorBold,
    PiBuildingsBold,
    PiPencilSimpleBold,
    PiPlusBold,
} from 'react-icons/pi';
import { Select } from '@v2/design-system';
import api from '@v2/lib/api';
import RichListShell, { BadgePill, PaginationMeta } from '../components/RichListShell';

type Client = {
    id: number;
    name: string;
    legal_name: string | null;
    cnpj: string | null;
    cnae: string | null;
    email: string | null;
    phone: string | null;
    city: string | null;
    state: string | null;
    logo_url: string | null;
    parent_name: string | null;
    shipments_count: number;
    units_count: number;
    active: boolean;
};

type ApiResponse = {
    data: Client[];
    meta: PaginationMeta;
};

type ClientStats = {
    total: number;
    ativos: number;
    inativos: number;
    embarques_abertos: number;
    clientes_com_embarques: number;
    grupos: number;
};

const FALLBACK = '/img/favicon.png';

/* ─── KPI Card ──────────────────────────────────────────────── */
function KpiCard({
    icon,
    label,
    value,
    sub,
    loading,
    accent,
}: {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    sub?: string;
    loading?: boolean;
    accent?: 'gold' | 'emerald' | 'sky' | 'violet';
}) {
    const accentMap = {
        gold:    'border-amber-200 dark:border-amber-500/20 [&_.icon-wrap]:bg-amber-50 [&_.icon-wrap]:text-amber-500 dark:[&_.icon-wrap]:bg-amber-500/10 dark:[&_.icon-wrap]:text-amber-400',
        emerald: 'border-emerald-200 dark:border-emerald-500/20 [&_.icon-wrap]:bg-emerald-50 [&_.icon-wrap]:text-emerald-500 dark:[&_.icon-wrap]:bg-emerald-500/10 dark:[&_.icon-wrap]:text-emerald-400',
        sky:     'border-sky-200 dark:border-sky-500/20 [&_.icon-wrap]:bg-sky-50 [&_.icon-wrap]:text-sky-500 dark:[&_.icon-wrap]:bg-sky-500/10 dark:[&_.icon-wrap]:text-sky-400',
        violet:  'border-violet-200 dark:border-violet-500/20 [&_.icon-wrap]:bg-violet-50 [&_.icon-wrap]:text-violet-500 dark:[&_.icon-wrap]:bg-violet-500/10 dark:[&_.icon-wrap]:text-violet-400',
    };
    return (
        <div className={`flex items-center gap-4 rounded-2xl border bg-white p-5 dark:bg-zinc-900 ${accentMap[accent ?? 'gold']}`}>
            <div className="icon-wrap flex size-12 shrink-0 items-center justify-center rounded-xl">
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{label}</p>
                {loading ? (
                    <div className="mt-1 h-7 w-14 animate-pulse rounded-md bg-gray-100 dark:bg-zinc-800" />
                ) : (
                    <p className="text-2xl font-black text-gray-900 dark:text-white">{value}</p>
                )}
                {sub && !loading && (
                    <p className="mt-0.5 text-[11px] text-gray-400 dark:text-zinc-600">{sub}</p>
                )}
            </div>
        </div>
    );
}

function fmtCnpj(v: string | null) {
    if (!v) return null;
    const d = v.replace(/\D/g, '');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return v;
}

export default function ClientListPage() {
    const [search, setSearch] = useState('');
    const [active, setActive] = useState<string | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(5);
    const navigate = useNavigate();

    // Reset para página 1 quando filtros ou per_page mudam
    useEffect(() => { setPage(1); }, [search, active, perPage]);

    const params = useMemo(() => {
        const p: Record<string, string> = { page: String(page), per_page: String(perPage) };
        if (search) p.search = search;
        if (active !== undefined) p.active = active;
        return p;
    }, [search, active, page, perPage]);

    const { data: statsData, isLoading: statsLoading } = useQuery({
        queryKey: ['clients-stats'],
        queryFn: async () => (await api.get<ClientStats>('/clients/stats')).data,
        staleTime: 60_000,
    });

    const { data, isLoading } = useQuery({
        queryKey: ['clients', params],
        queryFn: async () => (await api.get<ApiResponse>('/clients', { params })).data,
        placeholderData: (prev) => prev,
    });

    const rows = data?.data ?? [];

    return (
        <div className="space-y-6">
            {/* ── KPI Cards ─────────────────────────────────── */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KpiCard
                    icon={<PiUsersBold className="size-6" />}
                    label="Total de clientes"
                    value={statsData?.total ?? 0}
                    sub={`${statsData?.inativos ?? 0} inativo(s)`}
                    loading={statsLoading}
                    accent="gold"
                />
                <KpiCard
                    icon={<PiCheckCircleBold className="size-6" />}
                    label="Clientes ativos"
                    value={statsData?.ativos ?? 0}
                    sub={`${statsData?.total ? Math.round((statsData.ativos / statsData.total) * 100) : 0}% da base`}
                    loading={statsLoading}
                    accent="emerald"
                />
                <KpiCard
                    icon={<PiAnchorBold className="size-6" />}
                    label="Processos em aberto"
                    value={statsData?.embarques_abertos ?? 0}
                    sub="embarques em andamento"
                    loading={statsLoading}
                    accent="sky"
                />
                <KpiCard
                    icon={<PiBuildingsBold className="size-6" />}
                    label="Redes / Grupos"
                    value={statsData?.grupos ?? 0}
                    sub="franqueadoras com unidades"
                    loading={statsLoading}
                    accent="violet"
                />
            </div>

            <RichListShell<Client>
            title="Clientes"
            breadcrumb={[{ name: 'Cadastros' }, { name: 'Clientes' }]}
            headerActions={
                <button
                    type="button"
                    onClick={() => navigate('/clients/new')}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white transition hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900 dark:hover:bg-amber-500"
                >
                    <PiPlusBold className="size-3.5" />
                    Novo cliente
                </button>
            }
            search={search}
            onSearch={setSearch}
            isLoading={isLoading}
            activeFiltersCount={active !== undefined ? 1 : 0}
            filtersPanel={
                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <Select
                        label="Status"
                        options={[
                            { label: 'Todos', value: '' },
                            { label: 'Ativos', value: '1' },
                            { label: 'Inativos', value: '0' },
                        ]}
                        value={
                            active === undefined
                                ? { label: 'Todos', value: '' }
                                : { label: active === '1' ? 'Ativos' : 'Inativos', value: active }
                        }
                        onChange={(o: { value: string } | null) => setActive(o?.value || undefined)}
                    />
                </div>
            }
            rows={rows}
            rowKey={(r) => r.id}
            detailPath={(r) => `/clients/${r.id}`}
            pagination={data?.meta}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            actionsRender={(r) => (
                <button
                    type="button"
                    title="Editar"
                    onClick={(e) => { e.stopPropagation(); navigate(`/clients/${r.id}/edit`); }}
                    className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-600 dark:border-zinc-700 dark:text-zinc-500 dark:hover:border-amber-500/30 dark:hover:bg-amber-500/10 dark:hover:text-amber-400"
                >
                    <PiPencilSimpleBold className="size-3.5" />
                </button>
            )}
            columns={[
                {
                    key: 'logo',
                    label: '',
                    width: '3.5rem',
                    render: (r) => (
                        <img
                            src={r.logo_url || FALLBACK}
                            alt=""
                            className="size-10 rounded-full border border-gray-100 bg-gray-50 object-contain p-0.5 dark:border-zinc-700 dark:bg-zinc-800"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = FALLBACK;
                            }}
                        />
                    ),
                },
                {
                    key: 'name',
                    label: 'Cliente',
                    render: (r) => (
                        <div>
                            <div className="font-semibold text-gray-800 dark:text-zinc-100">{r.name}</div>
                            {r.legal_name && (
                                <div className="text-xs text-gray-400 dark:text-zinc-500">{r.legal_name}</div>
                            )}
                            {r.parent_name && (
                                <div className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-600">
                                    Franquia de {r.parent_name}
                                </div>
                            )}
                        </div>
                    ),
                },
                {
                    key: 'cnpj',
                    label: 'CNPJ / CPF',
                    render: (r) => {
                        const formatted = fmtCnpj(r.cnpj);
                        return formatted ? (
                            <span className="font-mono text-xs text-gray-600 dark:text-zinc-400">{formatted}</span>
                        ) : <span className="text-gray-400">—</span>;
                    },
                },
                {
                    key: 'contact',
                    label: 'Contato',
                    render: (r) => (
                        <div className="flex flex-col gap-0.5">
                            {r.email && (
                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400">
                                    <PiEnvelopeBold className="size-3 shrink-0 text-gray-400" />
                                    <span className="max-w-[180px] truncate">{r.email}</span>
                                </div>
                            )}
                            {r.phone && (
                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400">
                                    <PiPhoneBold className="size-3 shrink-0 text-gray-400" />
                                    {r.phone}
                                </div>
                            )}
                            {!r.email && !r.phone && <span className="text-gray-400">—</span>}
                        </div>
                    ),
                },
                {
                    key: 'location',
                    label: 'Localização',
                    render: (r) => {
                        const t = [r.city, r.state].filter(Boolean).join(' / ');
                        return t ? (
                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400">
                                <PiMapPinBold className="size-3 shrink-0 text-gray-400" />
                                <BadgePill tone="info">{t}</BadgePill>
                            </div>
                        ) : <span className="text-gray-400">—</span>;
                    },
                },
                {
                    key: 'shipments_count',
                    label: 'Embarques',
                    align: 'center',
                    render: (r) => (
                        <div className="flex flex-col items-center gap-0.5">
                            <BadgePill tone="primary">{r.shipments_count}</BadgePill>
                            {(r.units_count ?? 0) > 1 && (
                                <span className="text-[10px] text-gray-400 dark:text-zinc-600">
                                    {r.units_count} unidades
                                </span>
                            )}
                        </div>
                    ),
                },
                {
                    key: 'active',
                    label: 'Status',
                    align: 'center',
                    render: (r) =>
                        r.active ? (
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
                                <PiCheckCircleBold className="size-3" />Ativo
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500">
                                <PiXCircleBold className="size-3" />Inativo
                            </span>
                        ),
                },
            ]}
            />

        </div>
    );
}
